import { useMutation } from '@apollo/client'
import { datadogLogs } from '@datadog/browser-logs'
import { useCallback, useContext, useState } from 'react'
import { useAppDispatch } from '../../../App.store'
import { TextEntryState } from '../../../data/gql-gen/questionnaire/graphql'
import { UPDATE_RESPONSE_OPTION } from '../../../data/gql/questionnaire/mutations/updateResponseOption'
import {
  DraftQuestionItem,
  DraftQuestionnaireEntry,
  QuestionSettingCode
} from '../../../data/model/questionnaire'
import useGetDraftQuestionnaire from '../../../hooks/questionnaire/useGetDraftQuestionnaire'
import useSetQuestionnaireSetting from '../../../hooks/questionnaire/useSetQuestionnaireSetting'
import { useOptionalWaveId } from '../../../hooks/useOptionalWaveId'
import { useProjectId } from '../../../hooks/useProjectId'
import { useSurveyId } from '../../../hooks/useSurveyId'
import QuestionnaireContext, {
  closeAutoSumDialog
} from '../Questionnaire.context'
import {
  responseOptionTextEntryToggled,
  setQuestionSetting
} from '../Questionnaire.slice'
import { getEntryId } from '../Questionnaire.utils'
import AutoSumDialogComponent from './AutoSumDialog.component'

// default target value for auto-sum (common for budget allocation: 100%)
const DEFAULT_AUTO_SUM_VALUE = '100'
// empty string, zero, or positive integers up to 6 digits
const VALID_AUTO_SUM_VALUE_REGEX = /^$|^0$|^[1-9]\d{0,5}?$/

interface Props {
  entry: DraftQuestionnaireEntry
}

const AutoSumDialog = ({ entry }: Props) => {
  const {
    dispatch: dispatchToContext,
    questionnaireState: { isAutoSumDialogOpened }
  } = useContext(QuestionnaireContext)

  const dispatch = useAppDispatch()
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()

  const { handleSettingChange } = useSetQuestionnaireSetting(entry)

  const [targetValue, setTargetValue] = useState(DEFAULT_AUTO_SUM_VALUE)

  const entryItem = entry.entryItem as DraftQuestionItem
  const responseOptions = entryItem.responseOptions

  const [updateResponseOptionMutation] = useMutation(UPDATE_RESPONSE_OPTION, {
    context: { clientName: 'questionnaire' }
  })

  const handleCloseDialog = useCallback(() => {
    dispatchToContext(closeAutoSumDialog())
  }, [dispatchToContext])

  const enableNumericEntryOnAllOptions = useCallback(async () => {
    const entryId = getEntryId(entry)

    const optionsToUpdate = responseOptions.filter(
      (ro) => ro.textEntryState !== TextEntryState.NumericEntryEnabled
    )

    // Optimistic UI update - dispatch all at once for instant feedback
    for (const responseOption of optionsToUpdate) {
      dispatch(
        responseOptionTextEntryToggled({
          questionLk: entryId,
          responseOptionLk: responseOption.responseOptionLk,
          textEntryState: TextEntryState.NumericEntryEnabled
        })
      )
    }

    // Update backend sequentially - we do not want this approach but backend is limited to update all the response options in batch so we process them one by one
    datadogLogs.logger.warn(
      '[AutoSumDialog] Updating response options sequentially',
      {
        responseOptionsCount: optionsToUpdate.length,
        entryId
      }
    )
    for (const responseOption of optionsToUpdate) {
      await updateResponseOptionMutation({
        variables: {
          input: {
            projectId,
            surveyId,
            waveId,
            entryId,
            responseOptionId: responseOption.responseOptionLk,
            textEntryState: TextEntryState.NumericEntryEnabled
          }
        }
      })
    }
  }, [
    dispatch,
    entry,
    projectId,
    responseOptions,
    surveyId,
    updateResponseOptionMutation,
    waveId
  ])

  const handleSaveAutoSum = useCallback(async () => {
    const entryId = getEntryId(entry)

    handleCloseDialog()

    // Save the auto-sum setting
    dispatch(
      setQuestionSetting({
        questionLk: entryId,
        code: QuestionSettingCode.AutoSumTarget,
        value: targetValue
      })
    )
    await handleSettingChange(
      [
        {
          settingCode: QuestionSettingCode.AutoSumTarget,
          settingValue: targetValue
        }
      ],
      'no-cache'
    )

    // Enable numeric entry on all response options
    await enableNumericEntryOnAllOptions()
  }, [
    dispatch,
    enableNumericEntryOnAllOptions,
    entry,
    handleCloseDialog,
    handleSettingChange,
    targetValue
  ])

  const handleChangeTargetValue = (value: string) => {
    if (VALID_AUTO_SUM_VALUE_REGEX.test(value)) {
      setTargetValue(value)
    }
  }

  if (!isAutoSumDialogOpened) {
    return null
  }

  return (
    <AutoSumDialogComponent
      targetValue={targetValue}
      savingDisabled={!targetValue || targetValue === '0'}
      onChange={handleChangeTargetValue}
      onClose={handleCloseDialog}
      onSave={handleSaveAutoSum}
    />
  )
}

const AutoSumDialogContainer = () => {
  const {
    questionnaireState: { currentAutoSumEntryNumber }
  } = useContext(QuestionnaireContext)
  const { flattenedEntries } = useGetDraftQuestionnaire()
  const currentEntry = flattenedEntries.find((entry) => {
    return entry.number === currentAutoSumEntryNumber
  })
  return currentEntry ? <AutoSumDialog entry={currentEntry} /> : null
}

export default AutoSumDialogContainer
